import { useCallback, useEffect, useState } from "react";
import { Input } from "../../../components/input";
import { Button } from "../../../components/button";
import { DialogContent, DialogFooter, DialogTitle } from "../../../components/dialog";
import { FormErrorText, RequiredLabel } from "../../../components/form";
import { useToast } from "../../../components/toast-container";
import BoxIcon from "../../../assets/icons/box-circle-small-blue.svg";
import CircleCheckIcon from "../../../assets/icons/check-circle-md-green.svg";
import type { RestockEGiftCardRequest } from "../types/eGiftCard.types";
import { useEGiftCardActions } from "../hooks/useEGiftCardActions";

type RestockEGiftCardFormProps = {
  eGiftCardId: string;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  dialogContentProps?: React.ComponentProps<typeof DialogContent>;
};

const EMPTY_E_GIFT_CARD: RestockEGiftCardRequest = {
  coupon_id: "",
  coupon_name: "",
  created_quantity: 0,
  available_quantity: 0,
  restock_quantity: null,
};

const RESTOCK_PRESETS = [10, 25, 50, 100] as const;

const RestockEGiftCardForm = ({ eGiftCardId, onClose, onSuccess, dialogContentProps }: RestockEGiftCardFormProps) => {
  const { fetchEGiftCardDetailForRestock, handleRestockEGiftCard } = useEGiftCardActions();
  const { addToast } = useToast();
  const [eGiftCard, setEGiftCard] = useState<RestockEGiftCardRequest>(EMPTY_E_GIFT_CARD);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadEGiftCard = useCallback(async () => {
    setLoading(true);
    const detail = await fetchEGiftCardDetailForRestock(eGiftCardId);
    if (detail) setEGiftCard(detail);
    setLoading(false);
  }, [eGiftCardId, fetchEGiftCardDetailForRestock]);

  useEffect(() => {
    void loadEGiftCard();
  }, [loadEGiftCard]);

  const quantity = eGiftCard.restock_quantity || 0;
  const canSubmit = Number.isInteger(quantity) && quantity > 0 && !loading && !submitting;
  const validationMessage = eGiftCard.restock_quantity != null && !canSubmit
    ? "Enter a whole number greater than 0."
    : undefined;

  const handleRestock = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const response = await handleRestockEGiftCard(eGiftCardId, quantity);
    if (response?.success) {
      setEGiftCard(response);
      setSaved(true);
      addToast({ type: "success", title: "Success!", message: response.message || "E-gift card has been restocked successfully" });
      await onSuccess();
    } else {
      addToast({ type: "warning", title: "Warning!", message: response?.message || "E-gift-card restock failed." });
    }
    setSubmitting(false);
  };

  const handleAddMore = () => {
    setSaved(false);
    void loadEGiftCard();
  };

  return (
    <DialogContent {...dialogContentProps} className="bg-white text-gray-900 dark:bg-gray-900 dark:text-white">
      <DialogTitle className="flex items-center">
        <img src={BoxIcon} alt="" />
        <div className="ml-4">
          <span className="block text-[23px] font-normal text-[#0A0A0A] dark:text-white">Restock Reward</span>
          <span className="block text-[12.3px] font-semibold text-[#4A5565] dark:text-gray-300">Target • {eGiftCard.coupon_name || "Loading..."}</span>
        </div>
      </DialogTitle>

      {saved && (
        <div className="my-5 flex flex-col items-center">
          <img className="mb-2 block w-[50px]" src={CircleCheckIcon} alt="" />
          <span className="mb-[2px] block text-[23px]">Reward added successfully!</span>
          <span className="block text-[16px] font-semibold text-[#75757D] dark:text-gray-300">
            {eGiftCard.available_quantity} e-gift cards are now available for redemption.
          </span>
        </div>
      )}

      {!saved && <span className="text-[16px] font-semibold text-[#75757D] dark:text-gray-300">Current Status</span>}
      <div className="mt-5 flex justify-between">
        <div className="w-[45%] rounded-[10px] bg-[#D3F5EE] p-[15px_10px_15px_30px] dark:bg-emerald-900/20">
          <span className="block text-[16px] text-[#4A5565] dark:text-gray-300">Available quantity</span>
          <span className="text-[23px] font-semibold">{loading ? "-" : eGiftCard.available_quantity}</span>
        </div>
        <div className="w-[45%] rounded-[10px] bg-[#D3F5EE] p-[15px_10px_15px_30px] dark:bg-emerald-900/20">
          <span className="block text-[16px] text-[#4A5565] dark:text-gray-300">Total ever created</span>
          <span className="text-[23px] font-semibold">{loading ? "-" : eGiftCard.created_quantity}</span>
        </div>
      </div>

      {!saved && (
        <div className="mt-5">
          <RequiredLabel htmlFor="restock-quantity" className="text-[16px] font-semibold text-[#75757D]" required>
            Add quantity
          </RequiredLabel>
          <Input
            id="restock-quantity"
            type="number"
            min={1}
            step={1}
            disabled={loading || submitting}
            className="no-spinner mt-2 block h-[50px] w-full text-[16px] dark:bg-gray-800 dark:border-gray-600"
            placeholder="Enter number to add"
            value={quantity > 0 ? quantity : ""}
            onChange={(event) => setEGiftCard((current) => ({
              ...current,
              restock_quantity: event.target.value ? Number(event.target.value.replace(/^0+(?=\d)/, "")) : null,
            }))}
          />
          <FormErrorText message={validationMessage} />
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Quick add quantities">
            {RESTOCK_PRESETS.map((preset) => (
              <Button
                key={preset}
                type="button"
                size="sm"
                variant="outline"
                disabled={loading || submitting}
                onClick={() => setEGiftCard((current) => ({ ...current, restock_quantity: preset }))}
              >
                +{preset}
              </Button>
            ))}
          </div>
        </div>
      )}

      <DialogFooter>
        <div className="flex w-full justify-between">
          <Button variant="outline" className="h-[46px] w-[45%] rounded-[30px]" onClick={saved ? handleAddMore : onClose} disabled={submitting}>
            {saved ? "Add More" : "Cancel"}
          </Button>
          <Button className="add-button h-[46px] w-[45%] rounded-[30px]" onClick={saved ? onClose : handleRestock} disabled={!saved && !canSubmit}>
            {saved ? "Done" : submitting ? "Restocking..." : "Restock"}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
};

export default RestockEGiftCardForm;
